<template>
  <b-card>
    <div>
      <!-- search input -->
      <div class="custom-search d-flex align-items-center justify-content-end">
        <div
          class="d-flex flex-column flex-sm-row align-items-center mb-1 justify-content-around"
        >
          <template>
            <b-button
              class="flex-shrink-0"
              v-ripple.400="'rgba(255, 255, 255, 0.15)'"
              variant="primary"
              v-on:click="showModal"
            >
              Create Status
            </b-button>
          </template>
        </div>
      </div>

      <!-- table -->
      <vue-good-table
          styleClass="vgt-table striped"
          mode="remote"
          @on-page-change="onPageChange"
          @on-sort-change="onSortChange"
          @on-column-filter="onColumnFilter"
          @on-per-page-change="onPerPageChange"
          :totalRows="totalRecords"
          :rows="rows"
          :sort-options="{
          enabled: false,
          multipleColumns: true,
          initialSortBy: [{ field: 'id', type: 'desc' }],
          }"
          :columns="columns"
          :pagination-options="{
          enabled: true,
          perPage: pageLength,
        }"
      >
        <template slot="table-row" slot-scope="props">
          <!-- status -->
          <template v-if="props?.column?.field === 'format_name'">
            <b>{{ props?.row?.name }}</b>
          </template>

          <template v-if="props?.column?.field === 'format_sequence'">
            <b-badge variant='light-success'>
              {{ props.row.sequence }}
            </b-badge>
          </template>


          <span v-if="props.column.field === 'format_status'">
            <b-badge :variant="statusVariant(props.row.status)">
              {{ props?.row?.status ? "Active" : "Inactive" }}
            </b-badge>
          </span>
          <template v-if="props?.column?.field === 'format_type'">
            <template v-if="props?.row?.type == 1">
              <b-badge variant="light-warning">General</b-badge>
            </template>
            <template v-else-if="props?.row?.type == 2">
              <b-badge variant="light-success">Success</b-badge>
            </template>
            <template v-else-if="props?.row?.type == 3">
              <b-badge variant="light-danger">Dead</b-badge>
            </template>
          </template>

          <template v-if="props.column.field === 'format_lead_count'">

            <b-badge class="cursor-pointer" @click="filterLead(props.row.id)" variant='light-primary'>
              {{ props.row.leads_count }}
            </b-badge>

          </template>

          <template v-if="props.column.field === 'format_color'">
            <b-badge
                pill
                class="badge"
                :style="{ color: 'white', backgroundColor: ( props.row.color_code ?? '#7367f0' ) }"
            >
              {{ props.row.color_name }}
            </b-badge>

          </template>

          <!-- Column: Action -->
          <span v-if="props.column.field === 'action'">

            <template v-if="$permissionAbility(LEAD_STATUS_EDIT, permissions)">
              <span @click="onShow(props.row)">
                <feather-icon
                    v-b-tooltip.hover
                    icon="Edit2Icon"
                    class="mr-50 custom-icon cursor-pointer"
                    title="Edit"
                    variant="warning"
                    size="16"
                />
              </span>
            </template>

            <template v-if="$permissionAbility(LEAD_STATUS_DELETE, permissions)">
              <span @click="onDelete(props.row)">
                <feather-icon
                    v-b-tooltip.hover
                    icon="TrashIcon"
                    class="mr-50 custom-icon cursor-pointer"
                    variant="danger"
                    title="Delete"
                    size="16"
                />
              </span>
            </template>

          </span>

          <!-- Column: Common -->
          <span v-else>
            {{ props.formattedRow[props.column.field] }}
          </span>
        </template>

        <!-- pagination -->
        <template slot="pagination-bottom" slot-scope="props">
          <div class="d-flex justify-content-between flex-wrap">
            <div class="d-flex align-items-center mb-0 mt-1">
              <span class="text-nowrap"> Showing 1 to </span>
              <b-form-select
                v-model="pageLength"
                :options="['10', '25', '50', '100', '500']"
                class="mx-1"
                @input="
                  (value) => props.perPageChanged({ currentPerPage: value })
                "
              />
              <span class="text-nowrap"> of {{ props.total }} entries </span>
            </div>
            <div>
              <b-pagination
                :value="1"
                :total-rows="props.total"
                :per-page="pageLength"
                first-number
                last-number
                align="right"
                prev-class="prev-item"
                next-class="next-item"
                class="mt-1 mb-0"
                @input="(value) => props.pageChanged({ currentPage: value })"
              >
                <template #prev-text>
                  <feather-icon icon="ChevronLeftIcon" size="18" />
                </template>
                <template #next-text>
                  <feather-icon icon="ChevronRightIcon" size="18" />
                </template>
              </b-pagination>
            </div>
          </div>
        </template>
      </vue-good-table>
    </div>

    <b-modal
      id="modal-status-form"
      centered
      :title="modelType == 'editModel' ? 'Edit Status' : 'Create Status'"
      hide-footer
      @hidden="hiddenModal"
      no-close-on-backdrop
    >
      <validation-observer ref="leadStatusValidation">
        <b-form v-on:submit.prevent="validationForm">
          <!-- name -->
          <b-form-group label="Title" label-for="name">
            <validation-provider
              #default="{ errors }"
              name="title"
              vid="name"
              rules="required|max:255"
            >
              <b-form-input
                id="name"
                type="text"
                v-model="name"
                :state="errors.length > 0 ? false : null"
                placeholder="Status"
              />
              <small class="text-danger">{{ errors[0] }}</small>
            </validation-provider>
          </b-form-group>

          <!-- Sequence-->
          <b-form-group label="Sequence" label-for="sequence">
            <ValidationProvider
              name="sequence"
              v-slot="{ errors }"
              vid="sequence"
            >
              <v-select
                @open="filteredLeadSequenceStatus"
                option-disabled="disable"
                id="sequence"
                placeholder="Select Sequence"
                v-model="selectSequence"
                :options="leadSequenceOptions"
                :reduce="(option) => option.value"
                label="name"
              />
              <small class="text-danger">{{ errors[0] }}</small>
            </ValidationProvider>
          </b-form-group>

          <!-- select status color-->
          <b-form-group label="Color" label-for="color_name">
            <ValidationProvider
              name="color_name"
              v-slot="{ errors }"
              vid="color_name"
            >
              <v-select
                id="color_code"
                placeholder="Select Color"
                v-model="selectColorValue"
                :options="selectColorOption"
                label="name"
              >
                <template #option="{ name, value }">
                  <b-badge pill :style="{ background: value }">{{
                    name
                  }}</b-badge>
                </template>
              </v-select>
              <small class="text-danger">{{ errors[0] }}</small>
            </ValidationProvider>
          </b-form-group>

          <!-- select status type -->
          <b-form-group label="Status Type" label-for="type">
            <ValidationProvider
              name="status type"
              v-slot="{ errors }"
              vid="type"
            >
              <v-select
                id="type"
                placeholder="Select Type"
                v-model="selectStatusTypeValue"
                :options="leadStatusTypeConstants"
                :reduce="(option) => option.value"
                label="name"
              />

              <small class="text-danger">{{ errors[0] }}</small>
            </ValidationProvider>
          </b-form-group>

          <!-- select status type-->
          <b-form-group label="Status" label-for="status">
            <ValidationProvider name="status" v-slot="{ errors }" vid="status">
              <v-select
                id="status"
                placeholder="Select Status"
                v-model="selectStatusValue"
                :options="statusValueOption"
                :reduce="(option) => option.value"
                label="name"
              />
              <small class="text-danger">{{ errors[0] }}</small>
            </ValidationProvider>
          </b-form-group>

          <!-- loading button -->
          <template v-if="isLeadStatusFormSubmitLoading">
            <b-button class="float-right" variant="primary" disabled>
              <b-spinner small />
              Loading...
            </b-button>
          </template>

          <!-- submit button -->
          <template v-else>
            <b-button
              type="submit"
              class="float-right"
              v-ripple.400="'rgba(255, 255, 255, 0.15)'"
              variant="primary"
            >
              Submit
            </b-button>
          </template>
        </b-form>
      </validation-observer>
    </b-modal>
  </b-card>
</template>

<script>
import {
  BCard,
  BAvatar,
  BBadge,
  BPagination,
  BFormGroup,
  BFormInput,
  BFormSelect,
  BDropdown,
  BDropdownItem,
  BButton,
  BForm,
  BModal,
  BSpinner, VBTooltip,
} from "bootstrap-vue";
import { VueGoodTable } from "vue-good-table";
import Ripple from "vue-ripple-directive";
import { ValidationProvider, ValidationObserver } from "vee-validate";
import { required, max, min } from "@validations";
import ToastificationContent from "@core/components/toastification/ToastificationContent.vue";
import { mapGetters } from "vuex";
import {
  LEAD_STATUS_ACCESS,
  LEAD_STATUS_CREATE,
  LEAD_STATUS_EDIT,
  LEAD_STATUS_DELETE,
} from "@/helpers/permissionsConstant";
import LvColorPicker from "lightvue/color-picker";
import { leadSequenceConstants } from "@/helpers/constant/leadSequenceConstant";
import { leadStatusTypeConstants } from "@/helpers/constant/leadStatusTypeConstant";

export default {
  name: "LeadStatusView",
  components: {
    LvColorPicker,
    BForm,
    BButton,
    BCard,
    VueGoodTable,
    BAvatar,
    BBadge,
    BPagination,
    BFormGroup,
    BFormInput,
    BFormSelect,
    BDropdown,
    BDropdownItem,
    ValidationProvider,
    ValidationObserver,
    BModal,
    BSpinner,
  },
  directives: {
    Ripple,
    'b-tooltip': VBTooltip,
  },
  data() {
    return {
      LEAD_STATUS_ACCESS,
      LEAD_STATUS_CREATE,
      LEAD_STATUS_EDIT,
      LEAD_STATUS_DELETE,
      selectSequence: "",
      leadSequenceOptions: [],
      selectStatusTypeValue: "",
      leadStatusTypeConstants,
      leadStatusId: "",
      modelType: "",
      name: "",
      selectColor: "",
      selectColorCode: "",
      selectColorValue: "",
      countDead: "",
      countSuccess: "",
      selectColorOption: [
        { name: "Medium Slate Blue", value: "#7368FF" },
        { name: "Tiger's Eye", value: "#D98A35" },
        { name: "Ocean Green", value: "#3FB8A3" },
        { name: "Royal Blue", value: "#4B72D9" },
        { name: "Mulberry", value: "#D14B8A" },
        { name: "Medium Sea Green", value: "#40A35B" },
        { name: "American Rose", value: "#D94245" },

        { name: "Lavender Blue", value: "#7D85FF" },
        { name: "Cornflower Blue", value: "#5A82FF" },
        { name: "Slate Blue", value: "#6A6EFF" },

        { name: "Mint Green", value: "#3FCB94" },
        { name: "Sea Green", value: "#38B290" },
        { name: "Aquamarine", value: "#5BC9A8" },

        { name: "Medium Purple", value: "#9370DB" },
        { name: "Amethyst", value: "#9966CC" },
        { name: "Blue Violet", value: "#8A2BE2" },
        { name: "Royal Purple", value: "#7851A9" },
        { name: "Lavender Indigo", value: "#9457EB" }
      ],
      selectStatusValue: "",
      statusValueOption: [
        {
          name: "Active",
          value: true,
        },
        {
          name: "Inactive",
          value: false,
        },
      ],

      pageLength: 10,
      columns: [
        {
          label: "Title",
          field: "format_name",
          sortable: false,
        },
        {
          label: "Phase",
          field: "format_sequence",
          sortable: false,
        },
        {
          label: "Lead Counts",
          field: "format_lead_count",
          sortable: false,
        },
        {
          label: "Color",
          field: "format_color",
          sortable: false,
        },
        {
          label: "Status",
          field: "format_status",
          sortable: false,
        },
        {
          label: "Type",
          field: "format_type",
          formatFn: this.formatFnTablePosition,
          sortable: false,
        },
        {
          label: "Action",
          field: "action",
          sortable: false,
        },
      ],
      rows: [],
      isLeadStatusFormSubmitLoading: false,
      totalRecords: 0,
      serverParams: {
        columnFilters: {},
        sort: [],
        page: 1,
        perPage: 10,
      },
    };
  },
  computed: {
    ...mapGetters({
      permissions: "userModule/getPermissions",
    }),
    statusVariant() {
      const statusColor = {
        true: "light-success",
        false: "light-danger",
      };
      return (status) => statusColor[status];
    },
  },
  async created() {
    try {
      this.loadItems();
    } catch (error) {
      this.$toast({
        component: ToastificationContent,
        props: {
          title: "Error",
          icon: "BellIcon",
          variant: "danger",
          text: error?.response?.data?.message,
        },
      });
    }
  },
  methods: {
    updateParams(newProps) {
      this.serverParams = Object.assign({}, this.serverParams, newProps);
    },
    onPageChange(params) {
      this.updateParams({ page: params.currentPage });
      this.loadItems();
    },

    onPerPageChange(params) {
      this.updateParams({ perPage: params.currentPerPage });
      this.loadItems();
    },

    onSortChange(params) {
      this.updateParams({
        sort: params,
      });
      this.loadItems();
    },

    onColumnFilter(params) {
      this.updateParams(params);
      this.loadItems();
    },
    filterLead(id){
      this.$store.dispatch("leadModule/setFilter", {
        key: "filterStatusId",
        value: id,
      });
      this.$router.push({ name: 'admin-lead-management' });
    },
    formatFnTableStatus(status) {
      return status ? "Active" : "Inactive";
    },
    formatFnTablePosition(final) {
      return final ? "Final" : "Not Final";
    },
    showModal() {
      this.$bvModal.show("modal-status-form");
    },
    hiddenModal() {
      this.$bvModal.hide("modal-status-form");
      this.resetModal();
    },
    resetModal() {
      this.modelType = "";
      this.name = "";
      this.selectSequence = "";
      this.selectColorValue = "";
      this.leadStatusId = "";
      this.selectStatusValue = "";
      this.selectStatusTypeValue = "";
    },
    onShow(value) {
      this.modelType = "editModel";
      this.leadStatusId = value?.id;
      this.name = value?.name;
      this.selectSequence = value?.sequence;
      this.selectColorValue = {
        name: value?.color_name,
        value: value?.color_code,
      };
      this.selectStatusTypeValue = value?.type;
      this.selectStatusValue = value?.status;
      this.showModal();
    },

    async onDelete(row) {
      this.$swal({
        title: "Warning!",
        text: `Are You Sure You Want To Delete ${row.name}?`,
        icon: "warning",
        customClass: {
          confirmButton: "btn btn-primary",
          cancelButton: "btn btn-outline-danger ml-1",
        },
        buttonsStyling: false,
        showCancelButton: true,
        confirmButtonText: "Ok",
        showLoaderOnConfirm: true,
      }).then(async (result) => {
        if (result.isConfirmed) {
          try {
            await this.$api.delete(`/api/lead-status/${row.id}`);
            this.loadItems();
            this.$toast({
              component: ToastificationContent,
              props: {
                title: "Success",
                icon: "BellIcon",
                variant: "success",
                text: "Lead Status successfully deleted",
              },
            });
          } catch (error) {
            this.$toast({
              component: ToastificationContent,
              props: {
                title: "Error",
                icon: "BellIcon",
                variant: "danger",
                text: error?.response?.data?.message,
              },
            });
          }
        }
      });
    },

    async getStatusItems(params) {
      return await this.$api.get(
          "api/lead-status",
          {
            params: {
              show: params.show,
              page: params.page,
              sort: params.sort,
              q: params.q,
            },
          }
      );
    },
    async getStatusTypeCount() {
      return await this.$api.get("api/lead-status/type-count");
    },

    async loadItems() {
      try {
        const [leadStatus, statusTypeCount] = await Promise.all([
          this.getStatusItems({
            show: this.serverParams.perPage,
            page: this.serverParams.page,
            sort: this.serverParams.sort,
            q: this.searchTerm,
          }),
          this.getStatusTypeCount(),
        ]);

        this.countDead = statusTypeCount?.data?.data?.countDead;
        this.countSuccess = statusTypeCount?.data?.data?.countSuccess;
        const data = leadStatus?.data?.data;
        const meta = leadStatus?.data?.meta;
        this.totalRecords = meta?.pagination?.total;
        this.rows = data;
      } catch (error) {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: "Warning",
            icon: "BellIcon",
            variant: "warning",
            text: error?.response?.data?.message,
          },
        });
      }
    },

    async updateLeadStatus() {
      this.isLeadStatusFormSubmitLoading = true;
      await this.$api.put(`/api/lead-status/${this.leadStatusId}`, {
        name: this.name,
        color_name: this.selectColorValue.name,
        color_code: this.selectColorValue.value,
        status: this.selectStatusValue,
        sequence: this.selectSequence,
        type: this.selectStatusTypeValue,
      });
      this.isLeadStatusFormSubmitLoading = false;
      this.loadItems();
      this.hiddenModal();
      this.$toast({
        component: ToastificationContent,
        props: {
          title: "Success",
          icon: "BellIcon",
          variant: "success",
          text: "Lead Status successfully updated",
        },
      });
    },
    validationForm: async function () {
      this.$refs.leadStatusValidation.validate().then(async (success) => {
        if (success) {
          try {
            this.$refs.leadStatusValidation.reset();
            if (this.modelType == "editModel") {
              if (
                this.selectStatusTypeValue == 2 ||
                this.selectStatusTypeValue == 3
              ) {
                let message = "";
                if (this.selectStatusTypeValue == 2) {
                  message = "Success";
                } else {
                  message = "Dead";
                }
                this.$swal({
                  title: "Warning!",
                  text: `This Lead Status Will Be Set As ${message}!`,
                  icon: "warning",
                  customClass: {
                    confirmButton: "btn btn-primary",
                    cancelButton: "btn btn-outline-danger ml-1",
                  },
                  buttonsStyling: false,
                  showCancelButton: true,
                  confirmButtonText: "Ok",
                  showLoaderOnConfirm: true,
                }).then(async (result) => {
                  if (result.isConfirmed) {
                    this.updateLeadStatus();
                  }
                });

                this.isLeadStatusFormSubmitLoading = false;
              } else {
                this.updateLeadStatus();
              }
            } else {
              this.isLeadStatusFormSubmitLoading = true;

              if (
                (this.selectStatusTypeValue == 3 && this.countDead >= 1) ||
                (this.selectStatusTypeValue == 2 && this.countSuccess >= 1)
              ) {
                let message = "";

                if (this.selectStatusTypeValue == 3) {
                  message = "Dead";
                } else if (this.selectStatusTypeValue == 2) {
                  message = "Success";
                }
                this.$swal({
                  title: "Error!",
                  html: `Status Type <b>'${message}'</b> Can't be Multiple`,
                  icon: "error",
                  customClass: {
                    confirmButton: "btn btn-primary",
                    cancelButton: "btn btn-outline-danger ml-1",
                  },
                  buttonsStyling: false,
                });
                this.isLeadStatusFormSubmitLoading = false;
              } else {
                await this.$api.post("/api/lead-status", {
                  name: this.name,
                  color_name: this.selectColorValue.name,
                  sequence: this.selectSequence,
                  color_code: this.selectColorValue.value,
                  status: this.selectStatusValue,
                  type: this.selectStatusTypeValue,
                });
                this.isLeadStatusFormSubmitLoading = false;
                this.hiddenModal();
                this.loadItems();
                this.$toast({
                  component: ToastificationContent,
                  props: {
                    title: "Success",
                    icon: "BellIcon",
                    variant: "success",
                    text: "Lead Status successfully created",
                  },
                });
              }
            }
          } catch (error) {
            this.isLeadStatusFormSubmitLoading = false;
            if (error?.response?.data?.message) {
              this.$toast({
                component: ToastificationContent,
                props: {
                  title: "Error",
                  icon: "BellIcon",
                  variant: "danger",
                  text: error?.response?.data?.message,
                },
              });
            }
            if (error?.response?.data?.errors) {
              this.$refs.leadStatusValidation.setErrors(
                error?.response?.data?.errors
              );
            }
          }
        }
      });
    },

    async filteredLeadSequenceStatus() {
      try {
        const { data } = await this.$api.get("/api/lead-sequences");
        const filteredStatus = leadSequenceConstants.filter(
          (item) => !data?.data?.includes(item.value)
        );
        this.leadSequenceOptions = filteredStatus;
      } catch (error) {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: "Warning",
            icon: "BellIcon",
            variant: "warning",
            text: error?.response?.data?.message,
          },
        });
      }
    },
  },
};
</script>
<style lang="scss">
@import "@core/scss/vue/libs/vue-good-table.scss";
</style>





